/**
 * Xero connection status UI.
 *
 * - <XeroConnectionChip> — an always-visible header pill showing the live Xero
 *   connection + a countdown to when the session refreshes (green when healthy,
 *   amber within the last hour, a one-click "refresh needed" button once expired).
 * - <XeroReconnectCard> — a friendly, business-toned reconnect prompt shown in place
 *   of the dashboard when the connection has lapsed. Reconnecting uses Xero Custom
 *   Connections (no login) so it really is one click.
 */
import { useEffect, useState } from 'react';
import './XeroConnectionStatus.css';

/** Warn (amber) once the session has under an hour left. */
const NEAR_EXPIRY_MS = 60 * 60 * 1000;

/** A ticking clock so the countdown updates without a re-fetch. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Human, business-friendly remaining time, e.g. "7h 28m" or "42m". */
function formatRemaining(ms: number): string {
  const totalMin = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

interface ChipProps {
  expiresAt: string | null;
  connecting: boolean;
  onRefresh: () => void;
}

export function XeroConnectionChip({ expiresAt, connecting, onRefresh }: ChipProps) {
  const now = useNow();

  if (connecting) {
    return (
      <span className="xero-conn xero-conn--connecting" role="status">
        <span className="xero-conn__spinner" aria-hidden="true">↻</span> Refreshing Xero…
      </span>
    );
  }
  if (!expiresAt) return null; // unknown / disconnected — handled by the reconnect card

  const remaining = new Date(expiresAt).getTime() - now;
  const exactTitle = `Xero session expires ${new Date(expiresAt).toLocaleString()}`;

  if (remaining <= 0) {
    return (
      <button
        type="button"
        className="xero-conn xero-conn--expired"
        onClick={onRefresh}
        title={exactTitle}
        aria-label="Xero connection expired — refresh now"
      >
        <span className="xero-conn__dot" aria-hidden="true" /> Xero — refresh needed
      </button>
    );
  }

  const near = remaining <= NEAR_EXPIRY_MS;
  return (
    <span
      className={`xero-conn ${near ? 'xero-conn--warn' : 'xero-conn--ok'}`}
      role="status"
      title={exactTitle}
    >
      <span className="xero-conn__dot" aria-hidden="true" />
      Xero connected · {near ? 'expires' : 'refreshes'} in {formatRemaining(remaining)}
    </span>
  );
}

interface CardProps {
  connecting: boolean;
  onRefresh: () => void;
  detail?: string | null;
}

export function XeroReconnectCard({ connecting, onRefresh, detail }: CardProps) {
  return (
    <div className="xero-reconnect" role="alert" aria-live="polite">
      <div className="xero-reconnect__icon" aria-hidden="true">⏰</div>
      <h2 className="xero-reconnect__title">Your Xero connection has expired</h2>
      <p className="xero-reconnect__message">
        Reconnect to load the latest figures — it only takes a second.
      </p>
      <button
        type="button"
        className="xero-reconnect__button"
        onClick={onRefresh}
        disabled={connecting}
      >
        {connecting ? 'Refreshing…' : 'Refresh connection'}
      </button>
      {detail ? <p className="xero-reconnect__hint">{detail}</p> : null}
    </div>
  );
}
